<script setup>
import { ref } from 'vue';
import { onBeforeMount, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import RecoverPassword from "./domain/recover-password";
import { validateEmail } from "@/utilities/common";

import AppFooter from "@/examples/PageLayout/Footer.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";

const body = document.getElementsByTagName("body")[0];
const store = useStore();
const router = useRouter();
const toggleDefaultLayout = () => store.commit("toggleDefaultLayout");

const email = ref('');
const errors = ref([]);
const message = ref('');
const loading = ref(false);  

onBeforeMount(() => {
  store.state.hideConfigButton = true;
  store.state.showNavbar = false;
  store.state.showSidenav = false;
    body.classList.remove("bg-gray-100");
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  toggleDefaultLayout();
  body.classList.add("bg-gray-100");
});


const handleSubmit = async () => {
  errors.value = [];
  message.value = '';
  loading.value = true;  

  // Validar el correo electrónico
  if (!validateEmail(email.value)) {
    errors.value.push('Por favor, introduce un correo válido.');
    loading.value = false;  
    return;
  }

  try {
    await RecoverPassword(email.value);  // Send the email to the API

    message.value = "Se ha enviado un correo electrónico con las instrucciones";

  } catch (error) {

    // En caso de no encontrar el usuario 
    if (error.message.includes("404")) {
      errors.value.push('No se encontró un usuario con este email');

    } else {
      errors.value.push('Error al conectar con el servidor: ' + (error.message));
    }

  } finally {
    loading.value = false; 
  }
};

const goToLogin = () => {
  router.push({ name: "Login" });
};
</script>

<template>
  <main class="main-content mt-0">
    <div class="page-header align-items-start min-vh-50 pt-5 pb-11">
      <span class="mask bg-gradient-dark opacity-6"></span>
    </div>

    <div class="container">
      <div class="row mt-lg-n10 mt-md-n11 mt-n12 justify-content-center">
        <div class="col-xl-8 col-lg-7 col-md-9 col-sm-12 mx-auto">
          <div class="card mb-5">
            <div class="card-header text-center">
              <h4 class="font-weight-bolder">Recuperar contraseña</h4>
              <p class="mb-0">Introduce tu correo electrónico</p>
            </div>

            <div class="card-body">
              <form @submit.prevent="handleSubmit">
                <div class="d-flex flex-column flex-md-row justify-content-between">
                  <div class="w-100 w-md-40 pe-2">
                    <div class="position-relative">
                      <argon-input
                        id="email"
                        v-model="email"
                        type="email"
                        placeholder="Correo Electrónico"
                        aria-label="email"
                        required
                      />
                      <div v-if="message" class="alert alert-success text-center">
                        {{ message }}
                      </div>

                      <div v-if="errors.length" class="error-messages text-danger">
                        <ul>
                          <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div class="w-100 w-md-60 ps-md-6 mt-3 mt-md-0">
                    <small class="text-muted">Pasos para recuperar tu contraseña:</small>
                    <div class="row list-row w-100">
                      <div class="col-lg-12 col-12 ps-0">
                        <ul class="password-requirements">
                          <li>1. Ingresa tu correo electrónico.</li>
                          <li>2. Recibirás un enlace de recuperación en tu correo.</li>
                          <li>3. Haz clic en el enlace para restablecer tu contraseña.</li>
                          <li>4. Introduce una nueva contraseña y confirma.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="text-center">
                  <argon-button
                    type="submit"
                    color="dark"
                    variant="gradient"
                    full-width
                    class="mt-3 mb-0"
                    :disabled="loading"
                  >
                    {{ loading ? "Procesando..." : "Enviar" }}
                  </argon-button>
                </div>

                <div class="text-center mt-3">
                  <argon-button color="light" full-width @click="goToLogin">
                    Volver al Inicio de Sesion
                  </argon-button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>

  <app-footer />
</template>

<style scoped>
.error-messages {
  color: red;
  margin-top: 10px;
}

.message {
  color: black;
  margin-top: 10px;
}

.page-header {
  background-image: url('@/assets/img/loginbg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.toggle-password {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1.2rem;
  color: #6c757d;
}

.password-requirements {
  list-style-type: none;
  padding-left: 15px;
}

.password-requirements li {
  font-size: 0.75rem;
  color: #6c757d;
  display: flex;
  padding-left: 10px;
  align-items: center;
  margin-bottom: 5px;
}

@media (max-width: 768px) {
  .page-header {
    min-height: 30vh;
    padding-top: 3rem;
    padding-bottom: 5rem;
  }

  .card-header h4 {
    font-size: 1.2rem;
  }

  .card-header p {
    font-size: 0.9rem;
  }

  .password-requirements li {
    font-size: 0.7rem;
  }
}
</style>

